/** Batch "Set mode" dialog for the subscriptions grid (a custom `dialog(ctx)`
 *  batch action) — flips selected subscriptions between the `log_only` ramp
 *  and full `process` delivery.
 */
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { useState } from 'react';

import type { SubscriptionMode } from '@/api/notificationSubscriptions';
import type { BatchActionContext } from '@/components/DataGrid/batchTypes';

const MODE_CHOICES: { value: SubscriptionMode; label: string }[] = [
  { value: 'log_only', label: 'Log only — record deliveries, do not process' },
  { value: 'process', label: 'Process — feed deliveries into the import pipeline' },
];

export function SetModeBatchDialog({ ctx }: { ctx: BatchActionContext }) {
  const [mode, setMode] = useState<SubscriptionMode>('log_only');

  const submit = () => {
    void ctx.runStandard({ mode });
    ctx.close();
  };

  return (
    <Dialog open onClose={ctx.close} fullWidth maxWidth="sm">
      <DialogTitle>Set delivery mode</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl size="small" fullWidth>
            <InputLabel id="set-mode-label">Mode</InputLabel>
            <Select
              labelId="set-mode-label"
              label="Mode"
              value={mode}
              onChange={(e) => setMode(e.target.value as SubscriptionMode)}
            >
              {MODE_CHOICES.map((c) => (
                <MenuItem key={c.value} value={c.value}>
                  {c.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              log_only is the rollout ramp — deliveries are authenticated and recorded but not
              processed. Switch to process once the recorded payloads look right.
            </FormHelperText>
          </FormControl>

          {mode === 'process' && (
            <Alert severity="warning">
              Processing is live: every delivery on these subscriptions will update bookings,
              orders, products or availability as it arrives.
            </Alert>
          )}

          {ctx.selection.mode === 'all_matching' && (
            <Alert severity="info">Acting on {ctx.matchedPhrase} subscriptions.</Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={ctx.close}>Cancel</Button>
        <Button variant="contained" onClick={submit}>
          Set mode for {ctx.count.toLocaleString()} subscription(s)
        </Button>
      </DialogActions>
    </Dialog>
  );
}
